import { AuthService } from './authService';
import {
  CompletePutAwayLineRequest,
  CompletePutAwayLineResponse,
  CompletePutAwayBulkRequest,
  CompletePutAwayBulkResponse
} from '../types/putaway';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

// Existing interfaces for the old put-away functionality
export interface PutAwayReservationDto {
  binCode: string;
  stcZoneID: string;
  resourceNo: number;
  stcBinGrp: number;
  resQuantity: number;
  quantity: number;
  pickableBinYesNo: boolean;
  dummy: boolean;
}

export interface CreatePutAwayRequest {
  companyNo: string;
  warehouseID: string;
  itemNo: string;
  binGrpNo: number;
  pickRuleNo: number;
  grShipmentNo: number;
  putAwayToPickableBinYesNo: boolean;
  quantity: number;
  // For Standard mode - direct receipt from POrder
  pOrderNo?: number;
  pOrderLineNo?: number;
}

export interface CreatePutAwayResponse {
  success: boolean;
  reservations: PutAwayReservationDto[];
  errorMessage?: string;
}

// Interfaces for batch put-away from relocation (Grunnlag)
export interface CreatePutAwayFromRelocationRequest {
  relocationIds: string[];
}

export interface CreatePutAwayFromRelocationResponse {
  success: boolean;
  stcOrderNo?: number;
  processedCount: number;
  failedCount: number;
  successfulRelIds: string[];
  failures: PutAwayFailure[];
  errorMessage?: string;
}

export interface PutAwayFailure {
  relId: string;
  itemNo: string;
  errorMessage: string;
}

// New interfaces for StcIntRelOrder (Put-Away Orders)
export interface StcIntRelOrderDto {
  id: number;
  companyNo: string;
  warehouseID: string;
  stcOrderNo: number;
  transDt: string;
  orderStatus: number;
  transType: number;
  operatorID?: string;
  operatorGroupID?: string;
  orderPriority: number;
  description1?: string;
  lockStatus: number;
  lockedBy?: string;
  sessionID?: string;
  externalSystemID?: string;
  created: string;
  lastUpdate?: string;
  createdBy?: string;
  updatedBy?: string;
  statusText: string;
  transTypeText: string;
  lineCount: number;
  isLocked: boolean;
}

export interface StcIntRelOrderLineDto {
  id: number;
  companyNo: string;
  warehouseID: string;
  stcOrderNo: number;
  stcLineNo: number;
  itemNo: string;
  quantity: number;
  fromBinCode?: string;
  toBinCode?: string;
  grShipmentNo?: number;
  stcBoxNo?: string;
  lineStatus: number;
  movedBy?: string;
  itemGrp1?: string;
  itemGrp2?: string;
  itemGrp3?: string;
  description1?: string;
  resourceNo?: number;
  stcZoneID?: string;
  transType: number;
  clientID?: string;
  quantityMoved?: number;
  itemBarCode?: string;
  externalSystemID?: string;
  externalSystemStatus?: string;
  resID?: number;
  binGrpNo?: number;
  sorting?: number;
  relID?: number;
  created: string;
  lastUpdate?: string;
  createdBy?: string;
  updatedBy?: string;
  statusText: string;
  transTypeText: string;
  remainingQuantity: number;
  isCompleted: boolean;
}

export interface StcIntRelOrderDetailsDto {
  header: StcIntRelOrderDto;
  lines: StcIntRelOrderLineDto[];
  totalLines: number;
  completedLines: number;
  totalQuantity: number;
  movedQuantity: number;
  completionPercentage: number;
}

export interface UpdateQuantityMovedRequest {
  quantityMoved: number;
  movedBy?: string;
}

export interface UpdateStcIntRelOrderStatusRequest {
  status: number;
}

export interface LockStcIntRelOrderRequest {
  operatorID?: string;
}

class PutAwayService {
  
  /**
   * Get all available put-away orders (IntRelOrders) by status
   */
  async getAvailablePutAwayOrders(): Promise<StcIntRelOrderDto[]> {
    try {
      // Get orders with status indicating they're ready for put-away
      // Using status 10 (assigned) as similar to picking status logic
      const response = await AuthService.authenticatedGet(
        `${API_BASE_URL}/intrelorders/status/10`
      );
      
      if (!response.ok) {
        throw new Error(`Failed to get put-away orders: ${response.status} ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error getting available put-away orders:', error);
      throw error;
    }
  }

  /**
   * Get put-away order details with all lines
   */
  async getPutAwayOrderDetails(stcOrderNo: number): Promise<StcIntRelOrderDetailsDto> {
    try {
      const response = await AuthService.authenticatedGet(
        `${API_BASE_URL}/intrelorders/${stcOrderNo}/details`
      );
      
      if (!response.ok) {
        throw new Error(`Failed to get put-away order details: ${response.status} ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error getting put-away order details:', error);
      throw error;
    }
  }

  /**
   * Lock a put-away order to current operator
   */
  async lockPutAwayOrder(stcOrderNo: number, operatorID?: string): Promise<void> {
    try {
      const body = operatorID ? { operatorID } : {};
      
      const response = await AuthService.authenticatedPost(
        `${API_BASE_URL}/intrelorders/${stcOrderNo}/lock`,
        body
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || `Failed to lock put-away order: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error locking put-away order:', error);
      throw error;
    }
  }

  /**
   * Unlock a put-away order
   */
  async unlockPutAwayOrder(stcOrderNo: number, operatorID?: string): Promise<void> {
    try {
      const body = operatorID ? { operatorID } : {};
      
      const response = await AuthService.authenticatedPost(
        `${API_BASE_URL}/intrelorders/${stcOrderNo}/unlock`,
        body
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || `Failed to unlock put-away order: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error unlocking put-away order:', error);
      throw error;
    }
  }

  /**
   * Update quantity moved for a specific put-away line
   */
  async updateQuantityMoved(stcOrderNo: number, stcLineNo: number, quantityMoved: number, movedBy?: string): Promise<void> {
    try {
      const request: UpdateQuantityMovedRequest = {
        quantityMoved,
        movedBy
      };

      const response = await AuthService.authenticatedPut(
        `${API_BASE_URL}/intrelorders/${stcOrderNo}/lines/${stcLineNo}/quantity`,
        request
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || `Failed to update quantity moved: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error updating quantity moved:', error);
      throw error;
    }
  }

  /**
   * Update put-away order status
   */
  async updatePutAwayOrderStatus(stcOrderNo: number, status: number): Promise<void> {
    try {
      const request: UpdateStcIntRelOrderStatusRequest = { status };

      const response = await AuthService.authenticatedPut(
        `${API_BASE_URL}/intrelorders/${stcOrderNo}/status`,
        request
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || `Failed to update put-away order status: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error updating put-away order status:', error);
      throw error;
    }
  }

  /**
   * Update put-away order line (for updating bin codes, etc.)
   */
  async updatePutAwayOrderLine(stcOrderNo: number, stcLineNo: number, updates: Partial<StcIntRelOrderLineDto>): Promise<StcIntRelOrderLineDto> {
    try {
      const response = await AuthService.authenticatedPut(
        `${API_BASE_URL}/intrelorders/${stcOrderNo}/lines/${stcLineNo}`,
        updates
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || `Failed to update put-away order line: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating put-away order line:', error);
      throw error;
    }
  }

  /**
   * Start put-away process (similar to start picking)
   */
  async startPutAway(stcOrderNo: number): Promise<void> {
    try {
      // Update status to "Under arbeid" (20) - similar to picking
      await this.updatePutAwayOrderStatus(stcOrderNo, 20);
    } catch (error) {
      console.error('Error starting put-away:', error);
      throw error;
    }
  }

  /**
   * Complete put-away process (Fullfør utplassering)
   * Sets all line statuses to 60 and OrderStatus to 30 (Fullført)
   */
  async completePutAway(stcOrderNo: number): Promise<void> {
    try {
      // Call backend endpoint that handles full completion:
      // - Updates all lines to LineStatus = 60
      // - Updates order to OrderStatus = 30 (Fullført)
      const response = await AuthService.authenticatedPost(
        `${API_BASE_URL}/intrelorders/${stcOrderNo}/finalize`,
        {}
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || `Failed to complete put-away: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error completing put-away:', error);
      throw error;
    }
  }

  /**
   * Complete a single put-away line with bin location and quantity
   * This updates both the line and ItemBins inventory via domain events
   * Also creates StcTransaction records for inventory tracking
   */
  async completePutAwayLineWithBin(
    stcOrderNo: number,
    stcLineNo: number,
    warehouseID: string,
    toBinCode: string,
    quantity: number
  ): Promise<CompletePutAwayLineResponse> {
    try {
      const request: CompletePutAwayLineRequest = {
        warehouseID,
        toBinCode,
        quantity
      };

      const response = await AuthService.authenticatedPost(
        `${API_BASE_URL}/intrelorders/${stcOrderNo}/lines/${stcLineNo}/complete-with-bin`,
        request
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || errorData?.errorMessage || `Failed to complete put-away line: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error completing put-away line with bin:', error);
      throw error;
    }
  }

  /**
   * Complete all pending lines in a put-away order to the same bin location
   * Bulk operation that updates ItemBins for all items via domain events
   * Also creates StcTransaction records for each line
   */
  async completePutAwayBulkToBin(
    stcOrderNo: number,
    warehouseID: string,
    toBinCode: string
  ): Promise<CompletePutAwayBulkResponse> {
    try {
      const request: CompletePutAwayBulkRequest = {
        warehouseID,
        toBinCode
      };

      const response = await AuthService.authenticatedPost(
        `${API_BASE_URL}/intrelorders/${stcOrderNo}/complete-all-to-bin`,
        request
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || errorData?.errorMessage || `Failed to complete bulk put-away: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error completing bulk put-away to bin:', error);
      throw error;
    }
  }

  /**
   * Get available put-away orders for operator (locked to current operator or available)
   */
  async getAvailablePutAwayOrdersForOperator(): Promise<StcIntRelOrderDto[]> {
    try {
      const response = await AuthService.authenticatedGet(
        `${API_BASE_URL}/intrelorders/available`
      );
      
      if (!response.ok) {
        throw new Error(`Failed to get available put-away orders for operator: ${response.status} ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error getting available put-away orders for operator:', error);
      throw error;
    }
  }

  /**
   * Create put-away orders from StcIntRelocation records (Grunnlag)
   * This implements the "Opprett utlegg på grunnlag" functionality
   */
  async createPutAwayFromRelocationBatch(relocationIds: string[]): Promise<CreatePutAwayFromRelocationResponse> {
    try {
      const request: CreatePutAwayFromRelocationRequest = {
        relocationIds
      };

      const response = await AuthService.authenticatedPost(
        `${API_BASE_URL}/putaway/create-from-relocation-batch`,
        request
      );

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.errorMessage || errorData.message || JSON.stringify(errorData);
        } catch (e) {
          try {
            const errorText = await response.text();
            errorMessage = errorText || errorMessage;
          } catch (textError) {
            errorMessage = `${errorMessage} ${response.statusText}`;
          }
        }
        throw new Error(`Failed to create put-away from relocation: ${errorMessage}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating put-away from relocation batch:', error);
      throw error;
    }
  }

  // EXISTING LEGACY PUT-AWAY FUNCTIONALITY - Keep for backwards compatibility
  /**
   * Kjører put-away logikk for en StcTrans linje (Legacy functionality)
   */
  async createPutAwayForStcIntRelOrderLine(request: CreatePutAwayRequest): Promise<CreatePutAwayResponse> {
    try {
      const response = await AuthService.authenticatedPost(
        `${API_BASE_URL}/putaway/create-for-stc-int-rel-order-line`,
        request
      );
      
      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.errorMessage || errorData.message || JSON.stringify(errorData);
        } catch (e) {
          // If JSON parsing fails, try to get text
          try {
            const errorText = await response.text();
            errorMessage = errorText || errorMessage;
          } catch (textError) {
            // If both fail, use the status message
            errorMessage = `${errorMessage} ${response.statusText}`;
          }
        }
        throw new Error(`Failed to create put-away: ${errorMessage}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Detaljert feil ved opprettelse av put-away:', error);
      console.error('Request data:', request);
      throw error;
    }
  }

  /**
   * Hjelpefunksjon for å bygge put-away request fra StcTrans data (Legacy functionality)
   */
  buildPutAwayRequest(
    stcTrans: any,
    companyNo: string,
    warehouseID: string,
    binGrpNo: number = 1,
    pickRuleNo: number = 1,
    putAwayToPickableBinYesNo: boolean = true
  ): CreatePutAwayRequest {
    return {
      companyNo,
      warehouseID,
      itemNo: stcTrans.itemNo,
      binGrpNo,
      pickRuleNo,
      grShipmentNo: stcTrans.grShipmentNo || 0,
      putAwayToPickableBinYesNo,
      quantity: stcTrans.quantity || 0
    };
  }
}

export const putAwayService = new PutAwayService();
